<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import { useMessagingStore } from '@/stores/messaging.store'
import { useUsersStore, useReservationsStore } from '@/stores'
import { storeToRefs } from 'pinia'
import moment from 'moment'
import MessageBubble from '@core/components/MessageBubble.vue'
import MemberDetailsDialog from '@core/components/MemberDetailsDialog.vue'
import CmsText from '@core/components/CmsText.vue'
import rmLogo from '@/assets/logo.svg'

const props = defineProps({
  conversationId: {
    type: String,
    default: null,
  },
  isRestaurantAdmin: {
    type: Boolean,
    default: false,
  },
  restaurantId: {
    type: String,
    default: null,
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
  compact: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['close'])

const messagingStore = useMessagingStore()
const usersStore = useUsersStore()
const reservationsStore = useReservationsStore()

const { messages, loadingMessages, sendingMessage, activeConversation } = storeToRefs(messagingStore)
const { myUser, users } = storeToRefs(usersStore)

const newMessageText = ref('')
const messagesEndRef = ref(null)
const composerRef = ref(null)
const viewingDetailsMemberId = ref(null)
const sendError = ref('')
const viewingDetailsMember = computed(() => {
  if (!viewingDetailsMemberId.value) return null
  
  return users.value.find(u => u._id === viewingDetailsMemberId.value) || null
})

function openMemberDetails(member) {
  // Ensure the participant is in the users store so Profile.vue's fetchUserById can enrich it
  const existing = users.value.find(u => u._id === member._id)
  if (!existing) {
    users.value.push(member)
  }
  viewingDetailsMemberId.value = member._id
}

function resizeTextarea() {
  const el = composerRef.value?.$el?.querySelector('textarea:not(.v-textarea__sizer)')
  if (!el) return
  el.style.height = 'auto'
  el.style.height = Math.min(el.scrollHeight, 160) + 'px'
}

watch(newMessageText, () => {
  nextTick(resizeTextarea)
})

const conversation = computed(() => activeConversation.value)

// The other participant (for member-member conv)
const participant = computed(() => {
  if (!conversation.value) return null
  if (conversation.value.type === 'member-restaurant') return null
  if (conversation.value.type === 'member-support') return null

  return (conversation.value.participants || [])[0] || null
})

const threadTitle = computed(() => {
  if (!conversation.value) return ''
  if (conversation.value.type === 'member-support') return 'RM Support - for help & questions'
  if (conversation.value.type === 'member-restaurant') {
    return conversation.value.restaurant?.name || 'Restaurant'
  }
  const p = participant.value
  if (p) return `${p.firstName || ''} ${p.lastName || ''}`.trim()
  const others = conversation.value.participants || []

  return others.map(x => x.firstName).join(', ')
})

function getGooglePhotoUrl(photoReference) {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY
  
  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=200&maxheight=200&photo_reference=${photoReference}&key=${apiKey}`
}

const threadAvatarUrl = computed(() => {
  if (!conversation.value) return null
  if (conversation.value.type === 'member-support') return null
  if (conversation.value.type === 'member-restaurant') {
    const r = conversation.value.restaurant
    if (!r) return null
    if (r.imageIds?.length) return mediaUrl(r.imageIds[0], 'thumb')
    const photoRef = r.googlePlaceDetails?.photos?.[0]?.photo_reference
    if (photoRef) return getGooglePhotoUrl(photoRef)
    
    return null
  }
  const imgId = participant.value?.profileImageId
  
  return imgId ? mediaUrl(imgId, 'thumb') : null
})

const threadAvatarInitials = computed(() => {
  const p = participant.value
  if (!p) return ''
  
  return `${p.firstName?.[0] || ''}${p.lastName?.[0] || ''}`
})

// Rich profile fields (member-member only)
const profileTitle = computed(() => {
  const p = participant.value
  if (!p) return ''
  if (p.title && p.company) return `${p.title} - ${p.company}`

  return p.title || p.company || ''
})

const profileTitle2 = computed(() => {
  const p = participant.value
  if (!p) return ''
  if (p.title2 && p.company2) return `${p.title2} - ${p.company2}`

  return p.title2 || p.company2 || ''
})

const participantMembershipType = computed(() => {
  const p = participant.value
  if (!p) return null
  
  return p.membershipType || null
})

// Build profile URL helpers inline (same logic as misc.js)
function linkedinUrl(id) { return `https://www.linkedin.com/in/${id}` }
function instagramUrl(id) { return `https://www.instagram.com/${id}` }
function twitterUrl(id) { return `https://x.com/${id}` }
function tiktokUrl(id) { return `https://www.tiktok.com/@${id}` }
function websiteUrl(url) {
  if (!url) return ''
  
  return url.startsWith('http') ? url : `https://${url}`
}

// Group messages with showSender + date separator metadata
const enrichedMessages = computed(() => {
  return messages.value.map((msg, i) => {
    const prev = messages.value[i - 1]
    const ownMsg = isOwnMessage(msg)

    let showSender = true
    if (prev) {
      const sameSender = prev.senderId?.toString() === msg.senderId?.toString()
        && prev.senderType === msg.senderType
      const withinWindow = msg.createdAt && prev.createdAt
        && moment(msg.createdAt).diff(moment(prev.createdAt), 'minutes') < 5
      if (sameSender && withinWindow) showSender = false
    }

    let showDateSeparator = false
    let dateLabel = ''
    if (!prev) {
      showDateSeparator = true
    } else {
      const prevDay = moment(prev.createdAt).startOf('day')
      const currDay = moment(msg.createdAt).startOf('day')
      if (!currDay.isSame(prevDay)) showDateSeparator = true
    }
    if (showDateSeparator && msg.createdAt) {
      const d = moment(msg.createdAt)
      if (d.isSame(moment(), 'day')) dateLabel = 'TODAY'
      else if (d.isSame(moment().subtract(1, 'day'), 'day')) dateLabel = 'YESTERDAY'
      else if (d.isAfter(moment().subtract(7, 'days'))) dateLabel = d.format('dddd').toUpperCase()
      else dateLabel = d.format('MMMM D, YYYY').toUpperCase()
    }

    return { ...msg, showSender, showDateSeparator, dateLabel, isOwn: ownMsg }
  })
})

watch(
  () => props.conversationId,
  async id => {
    if (id) {
      await messagingStore.loadConversation(id)
      scrollToBottom()
    }
  },
  { immediate: true }
)

watch(messages, () => {
  nextTick(scrollToBottom)
})

function scrollToBottom() {
  nextTick(() => {
    if (messagesEndRef.value) {
      messagesEndRef.value.scrollIntoView({ behavior: 'smooth' })
    }
  })
}

function isOwnMessage(message) {
  if (message.senderType === 'restaurant') return props.isRestaurantAdmin
  
  return message.senderId?.toString() === myUser.value?._id?.toString()
}

async function handleSend() {
  if (!newMessageText.value.trim() || sendingMessage.value) return
  const text = newMessageText.value.trim()
  newMessageText.value = ''
  sendError.value = ''
  try {
    if (props.isRestaurantAdmin && props.restaurantId) {
      await messagingStore.sendRaMessage(props.conversationId, props.restaurantId, text)
    } else {
      await messagingStore.sendMessage(props.conversationId, text)
    }
    scrollToBottom()
  } catch (err) {
    newMessageText.value = text
    const msg = err?.response?.data?.message || err?.message || ''
    if (msg.toLowerCase().includes('favorited') || msg.toLowerCase().includes('favorite')) {
      sendError.value = 'You need to have this restaurant favorited to reply.'
    } else if (msg.toLowerCase().includes('replies') || msg.toLowerCase().includes('paused') || msg.toLowerCase().includes('disabled')) {
      sendError.value = 'This restaurant has paused replies.'
    } else if (msg) {
      sendError.value = msg
    } else {
      sendError.value = 'Failed to send message. Please try again.'
    }
  }
}

async function handleDeleteMessage(messageId) {
  try {
    await messagingStore.deleteMessage(messageId)
  } catch (err) {
    console.error('Delete message error', err)
  }
}

async function newDraft() {
  await usersStore.showLoadingOverlay()
  try {
    if (!myUser.value) {
      await usersStore.loginNewDummyUser()
    }
    await usersStore.startNewReservation()
  } catch (error) {
    console.error('Error creating new draft:', error)
  } finally {
    await usersStore.hideLoadingOverlay()
  }
}

function handleKeydown(event) {
  if (event.key === 'Enter' && !event.shiftKey) {
    event.preventDefault()
    handleSend()
  }
}
</script>

<template>
  <div class="messaging-thread d-flex flex-column h-100">
    <!-- Header / Profile Panel -->
    <div class="thread-header pa-4">
      <div class="d-flex align-start gap-3">
        <!-- Avatar -->
        <VAvatar
          v-if="conversation && conversation.type === 'member-support'"
          size="56"
          color="white"
          class="flex-shrink-0"
        >
          <VImg
            :src="rmLogo"
            cover
          />
        </VAvatar>
        <VAvatar
          v-else-if="conversation && conversation.type !== 'member-support'"
          size="56"
          :color="conversation.type === 'member-restaurant' ? 'primary-light' : 'grey-lighten-3'"
          class="flex-shrink-0"
          :class="{ 'cursor-pointer': participant }"
          @click="participant ? openMemberDetails(participant) : null"
        >
          <VImg
            v-if="threadAvatarUrl"
            :src="threadAvatarUrl"
            cover
          />
          <VIcon
            v-else-if="conversation.type === 'member-restaurant'"
            color="primary"
            size="28"
          >
            mdi-silverware-fork-knife
          </VIcon>
          <span
            v-else-if="threadAvatarInitials"
            class="text-body-1 font-weight-medium"
            style="color: var(--ds-brand-primary-dark, #3F998F);"
          >{{ threadAvatarInitials }}</span>
          <VIcon
            v-else
            color="grey"
            size="28"
          >
            mdi-account
          </VIcon>
        </VAvatar>

        <!-- Name + profile info -->
        <div
          v-if="conversation"
          class="flex-1 min-w-0"
        >
          <!-- Name line (clickable to open profile dialog for member-member) -->
          <div class="d-flex align-center gap-1 flex-wrap">
            <a
              v-if="participant"
              class="text-subtitle-1 font-weight-semibold thread-profile-link"
              @click.prevent="openMemberDetails(participant)"
            >
              {{ threadTitle }}
            </a>
            <span
              v-else
              class="text-subtitle-1 font-weight-semibold"
            >{{ threadTitle }}</span>
            <VIcon
              v-if="participant?.verified && participantMembershipType === 'Platinum'"
              size="20"
              class="verified-icon"
              style="margin-top: -2px;"
            >
              material-symbols:verified
            </VIcon>
            <span
              v-if="participantMembershipType && participantMembershipType !== 'Standard'"
              class="text-sm"
            >
              ({{ participantMembershipType === 'Trial' ? 'Platinum' : participantMembershipType }})
            </span>
          </div>

          <!-- Title / Company -->
          <div
            v-if="profileTitle"
            class="text-sm mt-1"
          >
            {{ profileTitle }}
          </div>
          <div
            v-if="profileTitle2"
            class="text-sm"
          >
            {{ profileTitle2 }}
          </div>

          <!-- Support subtitle — new reservation CTA -->
          <div
            v-if="conversation.type === 'member-support'"
            class="d-flex align-center mt-1 cursor-pointer"
            @click="newDraft"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <ellipse
                cx="8"
                cy="8"
                rx="8"
                ry="8"
                fill="var(--ds-brand-primary-dark)"
              />
              <path
                d="M10.5261 7.99958H5.05243M7.78927 5.47327V10.5259"
                stroke="white"
                stroke-width="0.860438"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <span class="text-sm text-primary ml-1">Request reservation</span>
          </div>

          <!-- Restaurant market -->
          <div
            v-if="conversation.type === 'member-restaurant' && conversation.restaurant?.market"
            class="text-sm text-medium-emphasis"
          >
            {{ conversation.restaurant.market }}
          </div>

          <!-- Verified tags -->
          <div
            v-if="participant?.verifiedTags?.length"
            class="mt-1"
          >
            <MemberVerifiedTags :member="participant" />
          </div>

          <!-- Bio -->
          <div
            v-if="participant?.bio"
            class="text-sm user-bio mt-2"
          >
            {{ participant.bio }}
          </div>

          <!-- Stats (Favorites + Friends) -->
          <div
            v-if="participant"
            class="d-flex align-center gap-2 mt-2"
          >
            <div
              v-if="participant.favoritedRestaurantsCount"
              class="d-flex align-center gap-1"
            >
              <span class="font-weight-semibold">{{ participant.favoritedRestaurantsCount || '-' }}</span>
              <span class="text-xs">Favorites</span>
            </div>
            <div
              v-if="participant.membersWhoAreFollowingCount"
              class="d-flex align-center gap-1"
            >
              <span class="font-weight-semibold">{{ participant.membersWhoAreFollowingCount || '-' }}</span>
              <span class="text-xs">Connections</span>
            </div>
          </div>

          <!-- Social links -->
          <div
            v-if="participant?.profileLinkedInProfileId || participant?.profileInstagramProfileId || participant?.profileXProfileId || participant?.profileTikTokProfileId || participant?.profileWebsiteLink"
            class="d-flex align-center gap-2 mt-2"
          >
            <a
              v-if="participant.profileLinkedInProfileId"
              :href="linkedinUrl(participant.profileLinkedInProfileId)"
              target="_blank"
              class="social-link"
              @click.stop
            >
              <VIcon size="20">mdi-linkedin</VIcon>
            </a>
            <a
              v-if="participant.profileInstagramProfileId"
              :href="instagramUrl(participant.profileInstagramProfileId)"
              target="_blank"
              class="social-link"
              @click.stop
            >
              <VIcon size="20">mdi-instagram</VIcon>
            </a>
            <a
              v-if="participant.profileXProfileId"
              :href="twitterUrl(participant.profileXProfileId)"
              target="_blank"
              class="social-link"
              @click.stop
            >
              <VIcon size="20">simple-icons:x</VIcon>
            </a>
            <a
              v-if="participant.profileTikTokProfileId"
              :href="tiktokUrl(participant.profileTikTokProfileId)"
              target="_blank"
              class="social-link"
              @click.stop
            >
              <VIcon size="20">simple-icons:tiktok</VIcon>
            </a>
            <a
              v-if="participant.profileWebsiteLink"
              :href="websiteUrl(participant.profileWebsiteLink)"
              target="_blank"
              class="social-link"
              @click.stop
            >
              <VIcon size="20">mdi-web</VIcon>
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Messages -->
    <div class="thread-messages flex-1 overflow-y-auto py-2">
      <div
        v-if="loadingMessages"
        class="d-flex justify-center py-8"
      >
        <VProgressCircular
          indeterminate
          color="primary"
          size="32"
        />
      </div>

      <div
        v-else-if="enrichedMessages.length === 0"
        class="d-flex flex-column align-center justify-center h-100 text-medium-emphasis"
      >
        <VIcon
          size="40"
          class="mb-2"
        >
          mdi-chat-outline
        </VIcon>
        <span class="text-body-2">No messages yet. Say hello!</span>
      </div>

      <template v-else>
        <template
          v-for="msg in enrichedMessages"
          :key="msg._id"
        >
          <!-- Date separator -->
          <div
            v-if="msg.showDateSeparator"
            class="date-separator d-flex align-center gap-3 px-4 my-3"
          >
            <div class="date-separator-line flex-1" />
            <span class="text-caption text-medium-emphasis">{{ msg.dateLabel }}</span>
            <div class="date-separator-line flex-1" />
          </div>

          <MessageBubble
            :message="msg"
            :is-own="msg.isOwn"
            :show-sender="msg.showSender"
            :restaurant="conversation?.type === 'member-restaurant' ? conversation.restaurant : null"
            @delete="handleDeleteMessage"
          />
        </template>
      </template>

      <div ref="messagesEndRef" />
    </div>

    <!-- Member Profile Dialog -->
    <MemberDetailsDialog
      :member="viewingDetailsMember"
      @on-close="viewingDetailsMemberId = null"
    />

    <!-- Send error -->
    <VAlert
      v-if="sendError"
      type="error"
      variant="tonal"
      density="compact"
      closable
      class="mx-4 mb-2"
      style="font-size: 14px;"
      @click:close="sendError = ''"
    >
      {{ sendError }}
    </VAlert>

    <!-- Composer -->
    <div class="thread-composer px-4 py-3">
      <div class="composer-box pa-2">
        <VTextarea
          ref="composerRef"
          v-model="newMessageText"
          placeholder="Write a message…"
          rows="1"
          no-resize
          hide-details
          variant="plain"
          density="compact"
          class="composer-textarea"
          @keydown="handleKeydown"
        />
        <div class="d-flex align-center justify-end mt-1">
          <div class="d-flex align-center gap-3">
            <span class="text-caption text-disabled">Press Enter to send</span>
            <VBtn
              color="primary"
              :loading="sendingMessage"
              :disabled="!newMessageText.trim()"
              icon
              size="small"
              variant="flat"
              @click="handleSend"
            >
              <VIcon size="16">
                mdi-send
              </VIcon>
            </VBtn>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.messaging-thread {
  height: 100%;
}

.thread-header {
  border-bottom: 1px solid var(--ds-border-default);
  flex-shrink: 0;
}

.thread-messages {
  flex: 1;
  overflow-y: auto;
  min-height: 0;
}

.thread-composer {
  border-top: 1px solid var(--ds-border-default);
  flex-shrink: 0;
}

.composer-box {
  border: 1px solid var(--ds-border-default);
  border-radius: 8px;
  background: var(--ds-surface-card);
  transition: border-color 0.15s;
}

.composer-box:focus-within {
  border-color: var(--ds-brand-primary-dark, #3F998F);
}

.composer-textarea :deep(textarea) {
  padding: 4px 0;
  min-height: 28px !important;
  overflow-y: auto;
  resize: none;
  /* ≥16px keeps iOS Safari from zooming the page when the composer focuses. */
  font-size: 16px;
}

.date-separator-line {
  height: 1px;
  background: var(--ds-border-default);
}

.min-w-0 {
  min-width: 0;
}

.social-link {
  color: var(--ds-text-muted);
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  transition: color 0.1s;
}

.social-link:hover {
  color: var(--ds-brand-primary-dark, #3F998F);
}

.thread-profile-link {
  cursor: pointer;
  color: inherit;
  text-decoration: none;
}

.thread-profile-link:hover {
  text-decoration: underline;
}

.verified-icon {
  color: rgb(0, 149, 246);
}

.user-bio {
  max-width: 100ch;
}
</style>
